import { useCallback, useEffect, useRef } from 'react'

import {
  ModuleTypeEnum,
  PreferenceContextEnum,
  SocketEvent,
} from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'

interface UserPreferencesUpdatedPayload {
  id: string
  userId: string
  context: PreferenceContextEnum
  preferences: Record<string, unknown>
  organizationId: string
  moduleType?: ModuleTypeEnum
  surface?: string
}

interface UserPreferencesRealtimeOptions {
  enabled?: boolean
  /** Filter to only receive updates for a specific context */
  context?: PreferenceContextEnum
  /** Callback when preferences are updated */
  onPreferencesUpdated?: (data: UserPreferencesUpdatedPayload) => void
  /** Enable broadcast channel for cross-tab sync */
  enableBroadcastChannel?: boolean
}

/**
 * Hook for real-time user preferences synchronization
 * Listens to preference update events for the current user via socket events
 */
export function useUserPreferencesRealtime(
  options: UserPreferencesRealtimeOptions = {},
) {
  const {
    context,
    enabled = true,
    onPreferencesUpdated,
    enableBroadcastChannel = true,
  } = options

  const { socket, isReady, processSocketEvent } = useRealtime({
    enableBroadcastChannel: enabled && enableBroadcastChannel,
    suppressLogging: false,
  })

  // Keep ref for callback to avoid re-subscribing
  const callbackRef = useRef(onPreferencesUpdated)

  // Update ref when callback changes
  useEffect(() => {
    callbackRef.current = onPreferencesUpdated
  }, [onPreferencesUpdated])

  // Socket event handler
  const handlePreferencesUpdated = useCallback(
    (data: UserPreferencesUpdatedPayload) => {
      // Filter by context if specified
      if (context && data.context !== context) {
        return
      }

      processSocketEvent(
        SocketEvent.USER_PREFERENCES_UPDATED,
        'user_preferences',
        data.id,
        data,
      )
      callbackRef.current?.(data)
    },
    [context, processSocketEvent],
  )

  // Set up socket listeners
  useEffect(() => {
    if (!enabled || !socket || !isReady) {
      return
    }

    // Add socket event listener
    socket.on(SocketEvent.USER_PREFERENCES_UPDATED, handlePreferencesUpdated)

    return () => {
      socket.off(SocketEvent.USER_PREFERENCES_UPDATED, handlePreferencesUpdated)
    }
  }, [enabled, socket, isReady, handlePreferencesUpdated])

  return {
    isReady,
  }
}
